import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Loader2, ExternalLink, ShieldAlert } from "lucide-react"
import { getStoredAuthToken } from "modules/auth/api/authService"
import {
  buildAuditAppUrl,
  clearAuditToken,
  exchangeAuditToken,
  setAuditToken,
} from "modules/audit/api/auditBridgeService"
import { Alert, AlertDescription } from "shared/ui/alert"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"

const normalizeText = (value: string | null) => {
  if (!value) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const normalizePath = (value: string | null) => {
  const normalized = normalizeText(value)
  if (!normalized) return "/permit-audit"
  const withLeadingSlash = normalized.startsWith("/") ? normalized : `/${normalized}`
  return withLeadingSlash.toLowerCase()
}

export function AuditEntryPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [isOpening, setIsOpening] = useState(true)

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const requestedPath = useMemo(() => normalizePath(searchParams.get("path")), [searchParams])
  const requestedRoleId = useMemo(() => normalizeText(searchParams.get("roleId")), [searchParams])

  useEffect(() => {
    let isCancelled = false

    const openAuditApp = async () => {
      const sourceToken = getStoredAuthToken()
      if (!sourceToken) {
        if (!isCancelled) {
          setError("Missing current login session. Please login again before opening Audit.")
          setIsOpening(false)
        }
        return
      }

      setIsOpening(true)
      setError(null)

      try {
        clearAuditToken()
        const response = await exchangeAuditToken({
          sourceToken,
          requestedPath,
          requestedRoleId,
        })

        if (isCancelled) {
          return
        }

        setAuditToken(response.token!)
        window.location.assign(buildAuditAppUrl(response.redirectPath ?? requestedPath))
      } catch (err: any) {
        if (isCancelled) {
          return
        }

        setError(err?.message ?? "Unable to open Audit application.")
        setIsOpening(false)
      }
    }

    void openAuditApp()

    return () => {
      isCancelled = true
    }
  }, [requestedPath, requestedRoleId])

  return (
    <div className="mx-auto max-w-2xl py-10">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            {isOpening ? <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
            Open Audit Application
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isOpening ? (
            <p className="text-sm text-slate-600">
              We are verifying your current session and creating an Audit token before opening the sub-application.
            </p>
          ) : null}

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap gap-3">
            {!isOpening ? (
              <Button onClick={() => navigate(-1)} variant="outline">
                Back
              </Button>
            ) : null}
            {!isOpening ? (
              <Button onClick={() => window.location.assign(location.pathname + location.search)}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
